import {
  rtkErrIsErrDocumentNotReady,
  useErrDocumentNotReady,
  useSetErrDocumentNotReadyFromRtk,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  OfferDocument,
  OfferImmutabilityReason,
  useVbsOfferPdfDownloadMutation,
  useVbsOfferPdfPreviewQuery,
  useVbsOfferSetImmutableMutation,
} from "@/services/backend/vbs/offers/api/offer-api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  ContractDocument,
  useVbsContractDownloadPDFMutation,
  useVbsContractPreviewPDFQuery,
} from "@/services/backend/vbs/contracts/api/contract-api";
import { Offer } from "@/services/backend/vbs/offers/types/offer";
import { isZeroUUID } from "@/shared/lib/utilities/uuid";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function OfferPdf({ offer }: { offer: Offer }) {
  const { data, isLoading, error } = useVbsOfferPdfPreviewQuery({
    id: offer.id,
    document: OfferDocument.ALL,
  });

  const {
    data: contractData,
    isLoading: contractIsLoading,
    error: contractError,
  } = useVbsContractPreviewPDFQuery({
    id: offer.contractId,
    document: ContractDocument.ALL,
  });

  const { notReadyError, setNotReadyError } = useErrDocumentNotReady();
  const { user } = useAuth();

  return (
    <DocumentSheet>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <Tabs className="md:col-span-3" defaultValue="offer">
            <TabsList>
              <TabsTrigger value="offer">{t("Angebot")}</TabsTrigger>
              <TabsTrigger value="contract">{t("Vertrag")}</TabsTrigger>
            </TabsList>
            <TabsContent value="offer">
              <DocumentSheetPreview
                objectURL={data?.objectURL}
                isLoading={isLoading}
                error={error}
              />
            </TabsContent>
            <TabsContent value="contract">
              <RTKQueryErrorAlert error={contractError} />
              {isZeroUUID(offer.contractId) && (
                <div className="text-muted-foreground text-center">
                  {t("Kein Vertrag vorhanden")}
                </div>
              )}
              <DocumentSheetPreview
                objectURL={contractData?.objectURL}
                isLoading={contractIsLoading}
                error={contractError}
              />
            </TabsContent>
          </Tabs>
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={<DocumentNumberById documentId={offer.id} />}
            />
            <DocumentSheetActionsContent>
              {!offer.immutable && (
                <>
                  <DocumentNotReadyAlert
                    error={notReadyError}
                    document={t("Angebot")}
                  />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      "Um eine Angebotsnummer zu generieren, schreiben Sie das Angebot fest.",
                    )}
                  />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: offer.id,
                      reason: OfferImmutabilityReason.SetByUser,
                      setBy: user?.id || "",
                    }}
                    mutation={useVbsOfferSetImmutableMutation}
                    description={t(
                      "Das Angebot wird festgeschrieben und es wird eine Angebotsnummer generiert. Danach kann das Angebot nicht mehr geändert werden.",
                    )}
                    onError={(err) => {
                      if (rtkErrIsErrDocumentNotReady(err)) {
                        setNotReadyError(err.data);
                      }
                    }}
                  />
                  <Separator />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.OFFER}
                  text={t("Angebot")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.TREATMENT}
                  text={t("Leistungsverzeichnis")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.TREATMENT_BY_AREA}
                  text={t("Bereichsaufteilung")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ORDER}
                  text={t("Auftrag")}
                />
                <Separator className="my-2" />
                <DownloadContractButton
                  contractId={offer.contractId}
                  document={ContractDocument.ALL}
                  text={t("Vertrag mit LV")}
                />
                <DownloadContractButton
                  contractId={offer.contractId}
                  document={ContractDocument.CONTRACT}
                  text={t("Vertrag ohne LV")}
                />
                <DownloadContractButton
                  contractId={offer.contractId}
                  document={ContractDocument.TREATMENT}
                  text={t("LV für Vertrag")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  offerId,
  document: documentType,
  text,
}: {
  offerId: string;
  document: OfferDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsOfferPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useSetErrDocumentNotReadyFromRtk(error, () => {
    setOpen(false);
    reset();
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: offerId, document: documentType });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Angebot heruntergeladen wird, wird es festgeschrieben. Es kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DownloadContractButton({
  contractId,
  document: documentType,
  text,
}: {
  contractId: string;
  document: ContractDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsContractDownloadPDFMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useSetErrDocumentNotReadyFromRtk(error, () => {
    setOpen(false);
    reset();
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      id: contractId,
      document: documentType,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild disabled={isZeroUUID(contractId)}>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Vertrag herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Vertrag heruntergeladen wird, wird er festgeschrieben. Er kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
